import { AdditionalCostHistory } from './../../../shared/interfaces/cost-type-config.interface';
import { CostTypeConfigService } from '@app/shared/services/cost-type-config.service';
import { Component, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Table, TableModule } from 'primeng/table';
import { Select } from 'primeng/select';
import { Popover } from 'primeng/popover';
import {
    AddAdditionalCostProductRequest,
    AdditionalCostProductResponse,
    Provider,
    ShoppingCategory,
    ShoppingProduct,
    ShoppingProductByCategory
} from '@app/shared/interfaces/cost-type-config.interface';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

type DialogMode = 'add' | 'edit';

@Component({
    selector: 'app-additional-cost-management',
    imports: [
        ToggleSwitchModule,
        CommonModule,
        PopoverModule,
        TableModule,
        ProgressSpinnerModule,
        ButtonModule,
        Select,
        FormsModule,
        DialogModule,
        InputTextModule,
        ConfirmDialogModule,
        ColumnFilterComponent,
        PageHeaderComponent
    ],
    templateUrl: './additional-cost-management.component.html',
    styleUrl: './additional-cost-management.component.scss'
})
export class AdditionalCostManagementComponent {
    @ViewChild('dt') dt!: Table;
    @ViewChild('op') op!: Popover;

    additionalCosts: AdditionalCostProductResponse[] = [];
    categories: ShoppingCategory[] = [];
    productsByCategory: ShoppingProductByCategory[] = [];
    airlines: string[] = [];
    flightTypes: string[] = [];
    costTypeConfigService = inject(CostTypeConfigService);

    productsForSelectedCategory: ShoppingProduct[] = [];
    providers: Provider[] = [];
    loading = true;
    displayDialog = false;
    displayHistoryDialog = false;
    dialogMode: DialogMode = 'add';
    dialogHeader = '';

    isFlightTypeDisabled = false;
    isAirlineDisabled = false;
    isAdditionalRatioDisabled = false;
    isProviderDisabled = false;
    isFlightTypeRequired = false;
    isAirlineRequired = false;
    isAdditionalRatioRequired = false;
    isProviderRequired = false;
    showArchived = false;
    historyData: AdditionalCostHistory[] = [];

    formData = {
        id: null as number | null,
        categoryId: null as number | null,
        productId: null as number | null,
        airlineCode: null as string | null,
        flightType: null as string | null,
        priceAmount: null as number | null,
        costAmount: null as number | null,
        providerId: null as number | null,
        additionalRatio: null as number | null,
        showOnWeb: false
    };

    constructor(
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private cd: ChangeDetectorRef
    ) {}
    getInitalData() {
        this.showArchived = false;
        this.loadTable();
        this.loadModalInfo();
    }
    ngOnInit() {
        this.getInitalData();
    }

    toggleOverlay(event: Event) {
        this.op.toggle(event);
    }

    getHistoryProducts() {
        this.loading = true;
        this.costTypeConfigService
            .getArchivedProducts()
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (data) => (this.additionalCosts = data),
                error: (err) => console.error('Error fetching additional cost products', err)
            });
    }

    onToggleArchived(event: any) {
        if (this.showArchived) {
            this.getHistoryProducts();
        } else {
            this.getInitalData();
        }
    }
    exportXlsxCustom() {
        if (!this.additionalCosts?.length) return;
        import('xlsx')
            .then((XLSX) => {
                const header = [
                    'Airline',
                    'Product Name',
                    'Price Amount',
                    'Price Currency',
                    'Flight Type',
                    'Provider',
                    'Additional Ratio',
                    'Cost Amount',
                    'Cost Currency',
                    'Category'
                ];
                const rows = this.additionalCosts.map((r: any) => [
                    r.airlineCode || '',
                    r.name || '',
                    r.price?.amount ?? '',
                    r.price?.currency ?? '',
                    r.flightType || '',
                    r.provider?.code ?? '',
                    r.additionalRatio ?? '',
                    r.cost?.amount ?? '',
                    r.cost?.currency ?? '',
                    r.shoppingCategory?.name ?? ''
                ]);
                const wsData = [header, ...rows];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                const colWidths = [
                    { wch: 10 },
                    { wch: 20 },
                    { wch: 12 },
                    { wch: 8 },
                    { wch: 12 },
                    { wch: 12 },
                    { wch: 15 },
                    { wch: 12 },
                    { wch: 8 },
                    { wch: 15 }
                ];
                ws['!cols'] = colWidths;
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Additional Costs');
                const fileName = `additional-costs-${new Date().toISOString().split('T')[0]}.xlsx`;
                XLSX.writeFile(wb, fileName);
            })
            .catch((error) => {
                console.error('XLSX library not available:', error);
                this.exportCsvFallback();
            });
    }

    private exportCsvFallback() {
        const header = ['Airline', 'Product Name', 'Price', 'Flight Type', 'Provider', 'Additional Ratio', 'Cost', 'Category'];
        const rows = this.additionalCosts.map((r: any) => [
            r.airlineCode || '',
            r.name || '',
            `${r.price?.amount ?? ''} ${r.price?.currency ?? ''}`.trim(),
            r.flightType || '',
            r.provider?.code ?? '',
            r.additionalRatio ?? '',
            `${r.cost?.amount ?? ''} ${r.cost?.currency ?? ''}`.trim(),
            r.shoppingCategory?.name ?? ''
        ]);

        const csv = [header, ...rows]
            .map((row) => row.map((cell) => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `additional-costs-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    }

    onCategoryChange(categoryId: number | null) {
        const cat = this.categories.find((c) => c.id === categoryId);
        const ftReq = cat?.flightTypeRequirement ?? 'DISABLED';
        const acReq = cat?.airlineCodeRequirement ?? 'DISABLED';
        const prReq = cat?.providerRequirement ?? 'DISABLED';
        const arReq = cat?.additionalRatioRequirement ?? 'DISABLED';

        this.isFlightTypeDisabled = ftReq === 'DISABLED';
        this.isAirlineDisabled = acReq === 'DISABLED';
        this.isProviderDisabled = prReq === 'DISABLED';
        this.isAdditionalRatioDisabled = arReq === 'DISABLED';

        this.isFlightTypeRequired = ftReq === 'REQUIRED';
        this.isAirlineRequired = acReq === 'REQUIRED';
        this.isProviderRequired = prReq === 'REQUIRED';
        this.isAdditionalRatioRequired = arReq === 'REQUIRED';

        this.formData.flightType = this.isFlightTypeDisabled ? null : this.formData.flightType;
        this.formData.airlineCode = this.isAirlineDisabled ? null : this.formData.airlineCode;
        this.formData.providerId = this.isProviderDisabled ? null : this.formData.providerId;
        this.formData.additionalRatio = this.isAdditionalRatioDisabled ? null : this.formData.additionalRatio;
        this.productsForSelectedCategory = this.getProducts(categoryId);
        if (!this.productsForSelectedCategory.some((p) => p.id === this.formData.productId)) {
            this.formData.productId = null;
        }
    }

    hideDialog(frm?: NgForm) {
        this.displayDialog = false;
        this.resetFormDisabledOrRequirement();
        frm?.resetForm();
    }

    save(frm: NgForm) {
        if (frm.invalid) {
            frm.control.markAllAsTouched();
            return;
        }
        const request: AddAdditionalCostProductRequest = {
            productId: this.formData.productId ?? (null as any),
            providerId: this.formData.providerId ?? (null as any),
            airlineCode: this.formData.airlineCode ?? (null as any),
            flightType: this.formData.flightType ?? null,
            price: {
                amount: this.formData.priceAmount ?? 0,
                currency: 'EUR'
            },
            cost: {
                amount: this.formData.costAmount ?? 0,
                currency: 'EUR'
            },
            additionalRatio: this.formData.additionalRatio ?? 0,
            showOnWeb: this.formData.showOnWeb
        };
        if (this.dialogMode === 'add') {
            this.costTypeConfigService.addAdditionalCostProduct(request).subscribe({
                next: (res) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'The additional cost product has been created successfully.'
                    });
                    this.getInitalData();
                },
                error: (err) => {
                    console.error(err);
                }
            });
        } else {
            const updateRequest = {
                airlineCodeProductId: this.formData.id as number,
                productId: this.formData.productId ?? (null as any),
                providerId: this.formData.providerId ?? null,
                airlineCode: this.formData.airlineCode ?? null,
                flightType: this.formData.flightType ?? null,
                price: {
                    amount: this.formData.priceAmount ?? 0,
                    currency: 'EUR'
                },
                cost: {
                    amount: this.formData.costAmount ?? 0,
                    currency: 'EUR'
                },
                additionalRatio: this.formData.additionalRatio ?? 0,
                showOnWeb: this.formData.showOnWeb
            };
            this.costTypeConfigService.editAdditionalCostProduct(updateRequest).subscribe({
                next: (res) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'The additional cost product has been updated successfully.'
                    });
                    this.getInitalData();
                }
            });
        }
        this.displayDialog = false;
        this.resetFormDisabledOrRequirement();
    }

    resetFormDisabledOrRequirement() {
        this.isFlightTypeDisabled = false;
        this.isAirlineDisabled = false;
        this.isAdditionalRatioDisabled = false;
        this.isProviderDisabled = false;
        this.isFlightTypeRequired = false;
        this.isAirlineRequired = false;
        this.isAdditionalRatioRequired = false;
        this.isProviderRequired = false;
    }
    openDialog(mode: DialogMode, row?: AdditionalCostProductResponse) {
        this.dialogMode = mode;
        this.displayDialog = true;

        if (mode === 'add') {
            this.dialogHeader = 'Add New';
            this.formData = {
                id: null,
                categoryId: null,
                productId: null,
                airlineCode: null,
                flightType: null,
                priceAmount: null,
                costAmount: null,
                providerId: null,
                additionalRatio: null,
                showOnWeb: true
            };
            this.productsForSelectedCategory = [];
        } else {
            this.dialogHeader = 'Edit';
            const categoryId = row?.shoppingCategory?.id ?? null;
            const productId = this.findProductIdByName(categoryId, row?.name ?? null);

            this.formData = {
                id: row?.id ?? null,
                categoryId,
                productId,
                providerId: row?.provider?.id ?? null,
                airlineCode: row?.airlineCode ?? null,
                flightType: row?.flightType ?? null,
                priceAmount: row?.price?.amount ?? null,
                costAmount: row?.cost?.amount ?? null,
                additionalRatio: row?.additionalRatio ?? null,
                showOnWeb: row?.showOnWeb ?? false
            };

            this.productsForSelectedCategory = this.getProducts(categoryId);
            this.setRequirements(categoryId);
        }
    }

    archive(id: number) {
        this.confirmationService.confirm({
            header: 'Confirm Archive',
            message: 'Are you sure you want to send this item to archive?',
            icon: 'pi pi-exclamation-triangle',
            acceptIcon: 'pi pi-check',
            rejectIcon: 'pi pi-times',
            acceptLabel: 'Yes, Archive',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.costTypeConfigService.sendToArchive(id).subscribe({
                    next: (res) => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Item has been sent to archive successfully'
                        });
                        this.getInitalData();
                    },
                    error: (err) => {
                        console.error(err);
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to archive the item. Please try again.'
                        });
                    }
                });
            },
            reject: () => {}
        });
    }

    showHistory(additionalCost: AdditionalCostProductResponse) {
        this.displayHistoryDialog = true;
        this.costTypeConfigService.showHistories(additionalCost.id).subscribe({
            next: (data) => (this.historyData = data)
        });
    }

    hideDialogHistory() {
        this.displayHistoryDialog = false;
    }

    onToggleShowOnWeb(item: AdditionalCostProductResponse) {
        const updateRequest = {
            airlineCodeProductId: item.id,
            productId: this.findProductIdByName(item.shoppingCategory?.id ?? null, item.name) ?? (null as any),
            providerId: item.provider?.id ?? null,
            airlineCode: item.airlineCode ?? null,
            flightType: item.flightType ?? null,
            price: {
                amount: item.price?.amount ?? 0,
                currency: item.price?.currency ?? 'EUR'
            },
            cost: {
                amount: item.cost?.amount ?? 0,
                currency: item.cost?.currency ?? 'EUR'
            },
            additionalRatio: item.additionalRatio ?? null,
            showOnWeb: item.showOnWeb
        };
        this.costTypeConfigService.editAdditionalCostProduct(updateRequest).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Show on web ${item.showOnWeb ? 'enabled' : 'disabled'} successfully`
                });
                this.loadTable();
            },
            error: (err) => {
                console.error(err);
                item.showOnWeb = !item.showOnWeb;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update show on web status'
                });
            }
        });
    }

    getProducts(categoryId: number | null): ShoppingProduct[] {
        if (categoryId == null) return [];
        return this.productsByCategory.find((sp) => sp.shoppingCategory.id === categoryId)?.shoppingProductList ?? [];
    }

    private loadTable() {
        this.loading = true;
        this.costTypeConfigService
            .getAddtionalCostProducts()
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this.cd.detectChanges();
                })
            )
            .subscribe({
                next: (data) => {
                    this.additionalCosts = data;
                    this.cd.detectChanges();
                },
                error: (err) => console.error('Error fetching additional cost products', err)
            });
    }

    private loadModalInfo() {
        this.costTypeConfigService.getModalInfo().subscribe({
            next: (data) => {
                const payload = Array.isArray(data) ? data[0] : data;
                this.categories = payload?.categories ?? [];
                this.airlines = payload?.airlineCodes ?? [];
                this.flightTypes = payload?.flightTypes ?? [];
                this.providers = payload?.providers ?? [];
                this.productsByCategory = payload?.shoppingProductByCategoryList ?? [];
            },
            error: (err) => console.error('Error fetching modal info', err)
        });
    }

    private setRequirements(categoryId: number | null) {
        const cat = this.categories.find((c) => c.id === categoryId);
        const ftReq = cat?.flightTypeRequirement ?? 'DISABLED';
        const acReq = cat?.airlineCodeRequirement ?? 'DISABLED';
        const prReq = cat?.providerRequirement ?? 'DISABLED';
        const arReq = cat?.additionalRatioRequirement ?? 'DISABLED';

        this.isFlightTypeDisabled = ftReq === 'DISABLED';
        this.isAirlineDisabled = acReq === 'DISABLED';
        this.isProviderDisabled = prReq === 'DISABLED';
        this.isAdditionalRatioDisabled = arReq === 'DISABLED';

        this.isFlightTypeRequired = ftReq === 'REQUIRED';
        this.isAirlineRequired = acReq === 'REQUIRED';
        this.isProviderRequired = prReq === 'REQUIRED';
        this.isAdditionalRatioRequired = arReq === 'REQUIRED';
        if (this.isFlightTypeDisabled) this.formData.flightType = null;
        if (this.isAirlineDisabled) this.formData.airlineCode = null;
        if (this.isProviderDisabled) this.formData.providerId = null;
        if (this.isAdditionalRatioDisabled) this.formData.additionalRatio = null;
    }

    private findProductIdByName(categoryId: number | null, productName: string | null): number | null {
        if (!categoryId || !productName) return null;
        const list = this.getProducts(categoryId);
        return list.find((p) => p.name === productName)?.id ?? null;
    }
}
